import React from 'react'

import type { BadgeProps } from '@wartek-id/badge'
import { Badge } from '@wartek-id/badge'
import { Text } from '@wartek-id/text-v1'

import { usePelaksanaanPraktikKinerja } from 'app/KinerjaTimPenilaiPSPage/hooks/usePelaksanaanPraktikKinerja'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'

import type {
  StatusPelaksanaanPraktikKinerja,
  StatusPelaksanaanPraktikKinerjaMetadata,
} from 'types/kinerja-ps-pegawai'

export function PelaksanaanPraktikKinerjaStatusBadge({
  currentState,
  state,
  meta,
  disabled,
  done,
}: {
  currentState: StatusPelaksanaanPraktikKinerja
  state: StatusPelaksanaanPraktikKinerja
  meta?: StatusPelaksanaanPraktikKinerjaMetadata
  disabled?: boolean
  done?: boolean
}) {
  const {
    isDiskusiPersiapanFormSubmitted,
    isPendampinganSekolahFormSubmitted,
    isLoading,
  } = usePelaksanaanPraktikKinerja({ state: currentState })
  const { isPPKPengawasSekolahUrl } = useBasePath()

  const badgeProps = React.useMemo((): {
    text: string
    variant: BadgeProps['variant']
  } => {
    if (disabled) {
      switch (state) {
        case 'DISKUSI_TINDAK_LANJUT':
          return {
            text: `Berikan hasil ${
              isPPKPengawasSekolahUrl ? 'penilaian' : 'pemantauan'
            } berdasarkan proses diskusi`,
            variant: 'neutral',
          }
        case 'UPAYA_TINDAK_LANJUT':
          return {
            text: 'Pantau pelaksanaan upaya.',
            variant: 'informational',
          }
      }
    }

    if (done) {
      switch (state) {
        case 'DISKUSI_PERSIAPAN':
          return currentState === 'PENDAMPINGAN_SEKOLAH' &&
            !isPendampinganSekolahFormSubmitted
            ? {
                text: 'Cek pengisian',
                variant: 'informational',
              }
            : {
                text: 'Selesai',
                variant: 'success',
              }
        case 'PENDAMPINGAN_SEKOLAH':
          return currentState === 'DISKUSI_TINDAK_LANJUT'
            ? {
                text: 'Selesai. Survei otomatis dibagikan.',
                variant: 'success',
              }
            : { text: 'Selesai', variant: 'success' }
        case 'REFLEKSI_TINDAK_LANJUT_PENILAIAN':
          return {
            text: `Berikan hasil ${
              isPPKPengawasSekolahUrl ? 'penilaian' : 'pemantauan'
            } berdasarkan pengisian.`,
            variant: 'informational',
          }
        default:
          return { text: 'Selesai', variant: 'success' }
      }
    }

    switch (state) {
      case 'DISKUSI_PERSIAPAN':
        return isDiskusiPersiapanFormSubmitted
          ? {
              text: 'Cek pengisian',
              variant: 'neutral',
            }
          : {
              text: 'Diskusikan bersama Pengawas Sekolah',
              variant: 'neutral',
            }
      case 'PENDAMPINGAN_SEKOLAH':
        return isPendampinganSekolahFormSubmitted
          ? {
              text: 'Selesai. Survei otomatis dibagikan.',
              variant: 'success',
            }
          : {
              text: 'Pantau pendampingan',
              variant: 'informational',
            }
      case 'DISKUSI_TINDAK_LANJUT':
        return meta?.[state]?.isPenilaian
          ? {
              text: `Berikan hasil ${
                isPPKPengawasSekolahUrl ? 'penilaian' : 'pemantauan'
              } berdasarkan proses diskusi`,
              variant: 'informational',
            }
          : {
              text: 'Cek pengisian',
              variant: 'informational',
            }
      case 'DISKUSI_TINDAK_LANJUT_PENILAIAN':
        return {
          text: 'Selesai',
          variant: 'success',
        }
      case 'UPAYA_TINDAK_LANJUT':
        return {
          text: 'Cek isi upaya tindak lanjut',
          variant: 'informational',
        }
      case 'REFLEKSI_TINDAK_LANJUT':
        return {
          text: `Berikan hasil ${
            isPPKPengawasSekolahUrl ? 'penilaian' : 'pemantauan'
          } berdasarkan pengisian.`,
          variant: 'informational',
        }
      case 'REFLEKSI_TINDAK_LANJUT_PENILAIAN':
        return {
          text: `Berikan hasil ${
            isPPKPengawasSekolahUrl ? 'penilaian' : 'pemantauan'
          } berdasarkan pengisian.`,
          variant: 'informational',
        }
      case 'SELESAI':
        return { text: 'Selesai', variant: 'success' }
    }
  }, [
    currentState,
    disabled,
    done,
    isDiskusiPersiapanFormSubmitted,
    isPPKPengawasSekolahUrl,
    isPendampinganSekolahFormSubmitted,
    meta,
    state,
  ])

  if (!badgeProps || isLoading) {
    return null
  }

  if (disabled) {
    return <Text variant="body-sm">{badgeProps.text}</Text>
  }

  return (
    <Badge variant={badgeProps.variant} className="w-fit">
      {badgeProps.text}
    </Badge>
  )
}

export default PelaksanaanPraktikKinerjaStatusBadge
